import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Users, Search, RefreshCw, Plus, Circle, ChevronRight, Trash2, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL, adminFetch } from '@/lib/authFetch';
import { toast } from 'sonner';
import { validarCPF } from '@/utils/cpfValidator';

interface Client {
  id: number;
  nome: string;
  nome_completo?: string;
  email: string;
  cpf: string;
  telefone: string | null;
  plano: string;
  status: string;
  data_cadastro: string | null;
  is_verified: boolean;
  consultas: number;
  access_type?: string;
  trial_end_date?: string;
  last_login?: string;
}

interface Consulta {
  id: number;
  placa: string;
  data: string;
  data_hora: string;
}

const ClientManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // Estados para consultas do cliente
  const [clientConsultas, setClientConsultas] = useState<Consulta[]>([]);
  const [loadingConsultas, setLoadingConsultas] = useState(false);
  
  // Estados para criar cliente
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingClientId, setDeletingClientId] = useState<number | null>(null);
  const [cpfError, setCpfError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newClient, setNewClient] = useState({
    nome: '',
    email: '',
    cpf: '',
    senha: '',
    access_type: '7' // '1' a '7' para dias de trial, ou 'free' para liberado
  });

  const loadClients = async () => {
    try {
      const response = await adminFetch(`${API_BASE_URL}/admin/clients`);
      if (response.ok) {
        const data = await response.json();
        setClients(Array.isArray(data) ? data : []);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Erro ao carregar clientes');
        setClients([]);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar clientes');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
    // Atualizar a cada 30 segundos para mostrar clientes online
    const interval = setInterval(() => loadClients(), 30000);
    return () => clearInterval(interval);
  }, []);

  // Carregar consultas quando abrir detalhes de um cliente
  const loadClientConsultas = async (clientId: number) => {
    setLoadingConsultas(true);
    try {
      const response = await adminFetch(`${API_BASE_URL}/admin/clients/${clientId}/consultas`);
      if (response.ok) {
        const data = await response.json();
        setClientConsultas(data.consultas || []);
      }
    } catch {
      // Erro ao carregar consultas
    } finally {
      setLoadingConsultas(false);
    }
  };

  const handleOpenDetails = (client: Client) => {
    setSelectedClient(client);
    setDetailsOpen(true);
    loadClientConsultas(client.id);
  };

  // Verificar se cliente está online (último login nos últimos 5 minutos)
  const isOnline = (lastLogin?: string) => {
    if (!lastLogin) return false;
    const last = new Date(lastLogin);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return last > fiveMinutesAgo;
  };

  // Estatísticas
  const totalClients = clients.length;
  const onlineClients = clients.filter(c => isOnline(c.last_login)).length;
  const totalConsultas = clients.reduce((acc, c) => acc + (c.consultas || 0), 0);

  const getAccessBadge = (client: Client) => {
    const baseClass = "text-[11px] px-2 py-0.5";
    if (client.access_type === 'free') {
      return <Badge variant="outline" className={`${baseClass} border-gray-500/30 text-gray-300`}>Liberado</Badge>;
    }
    if (client.access_type?.startsWith('trial') && client.trial_end_date) {
      const endDate = new Date(client.trial_end_date);
      const today = new Date();
      const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) {
        return <Badge variant="outline" className={`${baseClass} border-red-500/30 text-red-400`}>Expirado</Badge>;
      }
      return <Badge variant="outline" className={`${baseClass} border-gray-500/30 text-gray-300`}>{diffDays}d trial</Badge>;
    }
    if (client.plano && client.plano !== 'Sem plano') {
      return <Badge variant="outline" className={`${baseClass} border-gray-500/30 text-gray-300`}>{client.plano}</Badge>;
    }
    return <span className="text-gray-600 text-[11px]">-</span>;
  };

  const timeAgo = (dateStr?: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 30) return `${diffDays}d`;
    return `${Math.floor(diffDays/30)}m`;
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteClient = async (clientId: number, clientName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Previne abrir o modal de detalhes
    
    if (!confirm(`Tem certeza que deseja excluir o cliente "${clientName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }
    
    setDeletingClientId(clientId);
    try {
      const response = await adminFetch(`${API_BASE_URL}/admin/clients/${clientId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Cliente excluído com sucesso');
        setSearchTerm('');
        setSelectedClient(null);
        setDetailsOpen(false);
        loadClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir cliente');
      }
    } catch (e) {
      toast.error('Erro ao excluir cliente');
    } finally {
      setDeletingClientId(null);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!newClient.email.trim()) {
      toast.error('E-mail é obrigatório');
      return;
    }
    if (!newClient.cpf.trim()) {
      toast.error('CPF é obrigatório');
      return;
    }
    // Validar CPF
    const cpfLimpo = newClient.cpf.replace(/\D/g, '');
    if (!validarCPF(cpfLimpo)) {
      setCpfError('CPF inválido');
      toast.error('CPF inválido. Verifique os dígitos.');
      return;
    }
    setCpfError('');
    if (!newClient.senha.trim() || newClient.senha.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return;
    }

    setCreating(true);
    try {
      // Converter access_type para o formato do backend
      const payload = {
        nome: newClient.nome,
        email: newClient.email,
        cpf: newClient.cpf,
        senha: newClient.senha,
        access_type: newClient.access_type === 'free' ? 'free' : 'trial',
        trial_days: newClient.access_type === 'free' ? null : parseInt(newClient.access_type)
      };

      const response = await adminFetch(`${API_BASE_URL}/admin/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Cliente criado com sucesso!');
        setCreateModalOpen(false);
        setNewClient({ nome: '', email: '', cpf: '', senha: '', access_type: '7' });
        loadClients();
        
        if (newClient.access_type !== 'free') {
          toast.info(`Acesso liberado por ${newClient.access_type} dia(s)`);
        } else {
          toast.info('Acesso liberado permanentemente');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar cliente');
      }
    } catch (e) {
      toast.error('Erro ao criar cliente');
    } finally {
      setCreating(false);
    }
  };

  const formatCPFInput = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 11);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}.${limited.slice(3)}`;
    if (limited.length <= 9) return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`;
  };

  const formatCPFMasked = (cpf: string) => {
    if (!cpf) return '-';
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11) return '***.***.***-**';
    const visible = cleanCPF.slice(6);
    return `***.***. ${visible.slice(0, 3)}-${visible.slice(3)}`;
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.cpf?.includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || 
                          (filterStatus === 'online' && isOnline(client.last_login)) ||
                          (filterStatus === 'offline' && !isOnline(client.last_login));
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-4">
      {/* Cards de Estatísticas - Discretos */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-protec-dark-light/50 border border-protec-gold/10 rounded-lg p-3 sm:p-4">
          <p className="text-gray-500 text-[10px] sm:text-xs">Total Clientes</p>
          <p className="text-xl sm:text-2xl font-semibold text-white">{totalClients}</p>
        </div>

        <div className="bg-protec-dark-light/50 border border-protec-gold/10 rounded-lg p-3 sm:p-4">
          <p className="text-gray-500 text-[10px] sm:text-xs">Online Agora</p>
          <p className="text-xl sm:text-2xl font-semibold text-white">{onlineClients}</p>
        </div>

        <div className="bg-protec-dark-light/50 border border-protec-gold/10 rounded-lg p-3 sm:p-4">
          <p className="text-gray-500 text-[10px] sm:text-xs">Total Consultas</p>
          <p className="text-xl sm:text-2xl font-semibold text-white">{totalConsultas}</p>
        </div>
      </div>

      {/* Header with Search and Filters */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                Gerenciamento de Clientes
              </CardTitle>
              <CardDescription className="text-gray-300 text-xs sm:text-sm mt-1">
                Visualize clientes, consultas realizadas e status online
              </CardDescription>
            </div>
            <Button 
              className="bg-protec-gold text-black hover:bg-protec-gold-light text-sm"
              onClick={() => setCreateModalOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Criar Cliente</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, email ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-protec-dark border-protec-gold/30 text-white text-sm"
                autoComplete="off"
              />
            </div>
              <select 
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-protec-dark border border-protec-gold/30 text-white rounded-md px-3 py-2 text-sm min-w-[100px]"
              >
              <option value="all">Todos</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              </select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Clientes */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardContent className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 text-protec-gold animate-spin" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-4 opacity-50" />
              <p className="text-sm">Nenhum cliente encontrado</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredClients.map((client) => (
                <div 
                  key={client.id} 
                  className="flex items-center justify-between p-3 bg-protec-dark rounded-lg border border-protec-gold/10 hover:border-protec-gold/30 transition-all cursor-pointer"
                  onClick={() => handleOpenDetails(client)}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Avatar com indicador online */}
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-protec-gold/20 flex items-center justify-center">
                        <span className="text-protec-gold text-sm font-semibold">
                          {client.nome?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      {isOnline(client.last_login) && (
                        <Circle className="w-3 h-3 text-green-500 fill-green-500 absolute -bottom-0.5 -right-0.5" />
                      )}
                      </div>
                      
                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium text-sm truncate">{client.nome}</p>
                      <p className="text-gray-500 text-xs truncate">{client.email}</p>
                      </div>
                    </div>
                    
                  {/* Stats - Alinhados */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-center hidden sm:block w-16">
                      <p className="text-white text-sm font-medium">{client.consultas || 0}</p>
                      <p className="text-gray-500 text-[10px]">consultas</p>
                        </div>
                    <div className="text-center hidden sm:block w-14">
                      <p className="text-gray-300 text-sm">{timeAgo(client.last_login)}</p>
                      <p className="text-gray-500 text-[10px]">acesso</p>
                        </div>
                    <div className="w-24 text-center">
                      {getAccessBadge(client)}
                      </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={(e) => handleDeleteClient(client.id, client.nome, e)}
                      disabled={deletingClientId === client.id}
                      title="Excluir cliente"
                    >
                      {deletingClientId === client.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes do Cliente com Histórico de Consultas */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-protec-gold/20 flex items-center justify-center">
                  <span className="text-protec-gold font-bold">
                    {selectedClient?.nome?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                {selectedClient && isOnline(selectedClient.last_login) && (
                  <Circle className="w-3 h-3 text-green-500 fill-green-500 absolute -bottom-0.5 -right-0.5" />
                )}
                </div>
                <div>
                <p>{selectedClient?.nome}</p>
                <p className="text-xs text-gray-400 font-normal">{selectedClient?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Info do Cliente */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-protec-dark p-3 rounded-lg">
                  <p className="text-gray-500 text-[10px]">CPF</p>
                  <p className="text-white text-sm">{formatCPFMasked(selectedClient.cpf)}</p>
                </div>
                <div className="bg-protec-dark p-3 rounded-lg">
                  <p className="text-gray-500 text-[10px]">Status</p>
                  <p className="text-white text-sm">
                    {isOnline(selectedClient.last_login) ? 'Online' : 'Offline'}
                  </p>
                </div>
                <div className="bg-protec-dark p-3 rounded-lg">
                  <p className="text-gray-500 text-[10px]">Acesso</p>
                  <p className="text-white text-sm">
                    {selectedClient.access_type === 'free' ? 'Liberado' : 
                     selectedClient.access_type?.startsWith('trial') ? 'Trial' : 
                     selectedClient.plano || '-'}
                  </p>
                </div>
                <div className="bg-protec-dark p-3 rounded-lg">
                  <p className="text-gray-500 text-[10px]">Último Acesso</p>
                  <p className="text-white text-sm">{selectedClient.last_login ? formatDateTime(selectedClient.last_login) : '-'}</p>
                </div>
              </div>

              {/* Histórico de Consultas */}
              <div className="bg-protec-dark rounded-lg border border-protec-gold/10">
                <div className="p-3 border-b border-protec-gold/10 flex items-center justify-between">
                  <h4 className="text-white font-medium text-sm">Histórico de Consultas</h4>
                  <span className="text-gray-400 text-xs">{clientConsultas.length} consultas</span>
                </div>
                
                <div className="max-h-[300px] overflow-y-auto">
                  {loadingConsultas ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                </div>
                  ) : clientConsultas.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">Nenhuma consulta realizada</p>
                </div>
                  ) : (
                    <div className="divide-y divide-protec-gold/5">
                      {clientConsultas.map((consulta) => (
                        <div key={consulta.id} className="px-3 py-2.5 flex items-center justify-between hover:bg-white/5">
                          <p className="text-white font-mono text-sm">{consulta.placa}</p>
                          <p className="text-gray-500 text-xs">{formatDateTime(consulta.data_hora)}</p>
                </div>
                      ))}
                </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Criar Cliente */}
      <Dialog open={createModalOpen} onOpenChange={(open) => {
        setCreateModalOpen(open);
        if (open) {
          setNewClient({ nome: '', email: '', cpf: '', senha: '', access_type: '7' });
          setCpfError('');
          setShowPassword(false);
        }
      }}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Cliente</DialogTitle>
            <DialogDescription className="text-gray-400">
              O cliente receberá um email com as credenciais
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300 text-sm">Nome do Cliente *</Label>
              <Input
                value={newClient.nome}
                onChange={(e) => setNewClient({...newClient, nome: e.target.value})}
                placeholder="Digite o nome completo"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                autoComplete="off"
              />
            </div>

            <div>
              <Label className="text-gray-300 text-sm">E-mail *</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                placeholder="email@exemplo.com"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                autoComplete="off"
              />
            </div>

            <div>
              <Label className="text-gray-300 text-sm">CPF *</Label>
              <Input
                value={newClient.cpf}
                onChange={(e) => {
                  const formatted = formatCPFInput(e.target.value);
                  setNewClient({...newClient, cpf: formatted});
                  // Validar CPF em tempo real
                  const cpfLimpo = formatted.replace(/\D/g, '');
                  if (cpfLimpo.length === 11) {
                    if (!validarCPF(cpfLimpo)) {
                      setCpfError('CPF inválido');
                    } else {
                      setCpfError('');
                    }
                  } else {
                    setCpfError('');
                  }
                }}
                placeholder="000.000.000-00"
                className={`bg-protec-dark border-protec-gold/30 text-white mt-1 ${cpfError ? 'border-red-500' : ''}`}
                autoComplete="off"
              />
              {cpfError && (
                <p className="text-red-400 text-xs mt-1">{cpfError}</p>
              )}
            </div>

            <div>
              <Label className="text-gray-300 text-sm">Senha Temporária *</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newClient.senha}
                  onChange={(e) => setNewClient({...newClient, senha: e.target.value})}
                  placeholder="••••••••"
                  className="bg-protec-dark border-protec-gold/30 text-white pr-10"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-protec-gold"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-gray-500 text-xs mt-1">Mínimo 6 caracteres</p>
            </div>

            <div>
              <Label className="text-gray-300 text-sm">Tipo de Acesso *</Label>
              <select
                value={newClient.access_type}
                onChange={(e) => setNewClient({...newClient, access_type: e.target.value})}
                className="w-full bg-protec-dark border border-protec-gold/30 text-white rounded-md px-3 py-2 mt-1"
              >
                <option value="1">1 dia</option>
                <option value="2">2 dias</option>
                <option value="3">3 dias</option>
                <option value="4">4 dias</option>
                <option value="5">5 dias</option>
                <option value="6">6 dias</option>
                <option value="7">7 dias</option>
                <option value="free">Acesso Liberado</option>
              </select>
                </div>

          </div>

          <DialogFooter>
            <Button 
              onClick={handleCreateClient}
              disabled={creating}
              className="bg-protec-gold text-black hover:bg-protec-gold-light w-full"
            >
              {creating ? 'Criando...' : 'Criar Cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientManagement;
